import type { MenuItem } from 'primevue/menuitem'
import { useAdminListLinkItems, useAdminListLinkCategories } from '@swipe4work/api-orval'
import { LinkItemType, type CategoryDto, type LinkItemCategoryDto, type LinkItemDto } from '@swipe4work/models-orval'
import { useMenuPreferences, subcategoryKey } from '@/shared/composables/UseMenuPreferences'
import { useAuthReady } from '@/shared/composables/UseAuthReady'
import { linkItemTypeIcon } from '@/features/dashboard/linkItemTypes'

const DETAIL_ROUTE = '/dashboard/DashboardDetail'
const LOOSE_SUBCATEGORY = 'Overig'

/** A subcategory group of link items — the unit shown in both the sidebar and the dashboard overview. */
export type DashboardSection = {
  key: string
  label: string
  items: LinkItemDto[]
}

/**
 * Builds the user's composed menu from {@link useMenuPreferences}. The menu is grouped by SUBCATEGORY
 * (not category) so an item that lives in several selected categories appears once, under a single
 * subcategory header. Each item is placed via its "primary" active link — the membership whose category
 * has the lowest SortOrder. Subcategories are ordered by SubcategoryOrder, items by ItemOrder. With
 * nothing configured the menu falls back to showing everything (still deduped). Hidden items are removed.
 */
export function useComposedMenu() {
  const { preferences } = useMenuPreferences()
  const authReady = useAuthReady()
  const router = useRouter()

  const { data: itemsData, isPending } = useAdminListLinkItems(
    computed(() => ({ limit: 200 })),
    {
      query: {
        staleTime: 60 * 1000,
        enabled: authReady,
      },
    },
  )
  const { data: categoriesData } = useAdminListLinkCategories({
    query: {
      staleTime: 60 * 1000,
      enabled: authReady,
    },
  })

  /*
   * Link items are configured through the admin UI and stored in the database, so a route name can go
   * stale (renamed/removed route, or a DB shared with a branch that still has it) independently of the
   * currently running frontend build. Resolving a named route that doesn't exist throws and crashes the
   * whole app (vue-router only 404s gracefully for path-based resolution) — drop such items instead.
   */
  function isResolvable(item: LinkItemDto): boolean {
    return item.type !== LinkItemType.InternalLink || !item.routeName || router.hasRoute(item.routeName)
  }

  const allItems = computed(() => (itemsData.value?.items ?? []).filter(isResolvable))
  const orderedCategories = computed<CategoryDto[]>(() => categoriesData.value?.categories ?? [])
  const categorySortOrder = computed(() => new Map(orderedCategories.value.map(c => [ c.id, c.sortOrder ])))

  const isConfigured = computed(() =>
    preferences.value.categoryIds.length > 0
    || preferences.value.subcategoryKeys.length > 0
    || preferences.value.linkItemIds.length > 0,
  )

  const selectedCategoryIds = computed(() => new Set(preferences.value.categoryIds))
  const selectedSubcategoryKeys = computed(() => new Set(preferences.value.subcategoryKeys))
  const selectedItemIds = computed(() => new Set(preferences.value.linkItemIds))

  const visibleItems = computed(() => {
    const hidden = new Set(preferences.value.hiddenLinkItemIds)

    return allItems.value.filter(item => !hidden.has(item.id))
  })

  function isLinkActive(link: LinkItemCategoryDto): boolean {
    return selectedCategoryIds.value.has(link.id)
      || selectedSubcategoryKeys.value.has(subcategoryKey(link.id, link.subcategory))
  }

  /** The category memberships of an item that the current selection includes (all of them as fallback). */
  function activeLinks(item: LinkItemDto): LinkItemCategoryDto[] {
    if (!isConfigured.value) {
      return item.categories
    }

    return item.categories.filter(isLinkActive)
  }

  /** The membership that decides where an item is placed: lowest category SortOrder among the given links. */
  function primaryLinkFrom(links: LinkItemCategoryDto[]): LinkItemCategoryDto | null {
    if (links.length === 0) {
      return null
    }

    return [ ...links ].sort((a, b) =>
      (categorySortOrder.value.get(a.id) ?? Number.MAX_SAFE_INTEGER)
      - (categorySortOrder.value.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    )[0]
  }

  function primaryLink(item: LinkItemDto): LinkItemCategoryDto | null {
    return primaryLinkFrom(activeLinks(item))
  }

  const displayItems = computed(() => {
    if (!isConfigured.value) {
      return visibleItems.value
    }

    return visibleItems.value.filter(item =>
      selectedItemIds.value.has(item.id) || item.categories.some(isLinkActive),
    )
  })

  /** Subcategory groups (deduped, ordered) + loose items with no shown category membership. */
  function composeGroups(items: LinkItemDto[], linkFor: (item: LinkItemDto) => LinkItemCategoryDto | null) {
    const grouped = new Map<string, {
      order: number,
      entries: {
        item: LinkItemDto,
        order: number
      }[]
    }>()
    const loose: LinkItemDto[] = []

    for (const item of items) {
      const link = linkFor(item)

      if (!link) {
        loose.push(item)
        continue
      }

      const subcategory = link.subcategory?.trim() || LOOSE_SUBCATEGORY
      const bucket = grouped.get(subcategory) ?? {
        order: link.subcategoryOrder,
        entries: [],
      }

      bucket.order = Math.min(bucket.order, link.subcategoryOrder)
      bucket.entries.push({
        item,
        order: link.itemOrder,
      })
      grouped.set(subcategory, bucket)
    }

    const groups = [ ...grouped.entries() ]
      .sort(([ aName, a ], [ bName, b ]) => a.order - b.order || aName.localeCompare(bName))
      .map(([ subcategory, bucket ]) => ({
        subcategory,
        items: bucket.entries
          .sort((x, y) => x.order - y.order || x.item.name.localeCompare(y.item.name))
          .map(e => e.item),
      }))

    return {
      groups,
      loose,
    }
  }

  /** The personalized composition driven by the user's menu preferences. */
  const composition = computed(() => composeGroups(displayItems.value, primaryLink))

  /**
   * Every item — regardless of menu preferences or hidden links — deduped the same way as the menu:
   * each item appears once, under the subcategory of its primary link (lowest category SortOrder).
   */
  const allComposition = computed(() => composeGroups(allItems.value, item => primaryLinkFrom(item.categories)))

  function navTarget(item: LinkItemDto): Partial<Pick<MenuItem, 'route' | 'url' | 'target'>> {
    if (item.type === LinkItemType.InternalLink && item.routeName) {
      return { route: { name: item.routeName } as any }
    }

    if (item.type === LinkItemType.Uri && item.uri) {
      return {
        url: item.uri,
        target: item.openInNewTab === false ? '_self' : '_blank',
      }
    }

    return {
      route: {
        name: DETAIL_ROUTE,
        params: { id: item.id },
      } as any,
    }
  }

  function toMenuItem(item: LinkItemDto): MenuItem {
    return {
      label: item.name,
      icon: item.icon || linkItemTypeIcon(item.type),
      ...navTarget(item),
    }
  }

  // The sidebar only contains items flagged ShowInMenu; everything else lives on the homepage only.
  const sidebarGroups = computed<MenuItem[]>(() => {
    const groups: MenuItem[] = composition.value.groups
      .map(group => ({
        label: group.subcategory,
        items: group.items.filter(item => item.showInMenu).map(toMenuItem),
      }))
      .filter(group => (group.items as MenuItem[]).length > 0)

    const looseMenuItems = composition.value.loose.filter(item => item.showInMenu)

    if (looseMenuItems.length > 0) {
      groups.push({
        label: 'Losse links',
        items: looseMenuItems.map(toMenuItem),
      })
    }

    return groups
  })

  function toSections({ groups, loose }: ReturnType<typeof composeGroups>): DashboardSection[] {
    const sections: DashboardSection[] = groups.map(group => ({
      key: `sub-${group.subcategory}`,
      label: group.subcategory,
      items: group.items,
    }))

    if (loose.length > 0) {
      sections.push({
        key: 'loose',
        label: 'Losse links',
        items: loose,
      })
    }

    return sections
  }

  const dashboardSections = computed<DashboardSection[]>(() => toSections(composition.value))
  const allDashboardSections = computed<DashboardSection[]>(() => toSections(allComposition.value))

  return {
    isPending,
    isConfigured,
    allItems,
    categories: orderedCategories,
    displayItems,
    sidebarGroups,
    dashboardSections,
    allDashboardSections,
    navTarget,
  }
}
